/**
 * AdminClaims Component
 * Admin-only page for reviewing organization ownership claim requests.
 * When a user claims ownership of an unowned organization (typically a legacy
 * org imported from Kumu.io), an admin must approve or reject the claim.
 *
 * Key behaviors:
 * - Fetches paginated list of pending ownership claims from the admin API
 * - Each claim card shows: org info, claimant details, verification method,
 *   role at org, and time since request
 * - Approve/Reject buttons open a confirmation modal with optional admin notes
 * - Approving a claim makes the claimant the owner of the organization
 * - After review, the claims list is refreshed on the current page
 * - Verification badge: "Email Domain Match" (auto-verified) vs "Manual Verification"
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Spinner,
    Alert,
    Modal,
    Form,
    Pagination,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';

import { useAdminApi } from '@/shared/services/adminApi';
import './Admin.css';

export default function AdminClaims() {
    // ---- Claims List State ----
    const [claims, setClaims] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        pages: 1,
        total: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // ---- Review Modal State ----
    // The modal is shared for both approve and reject actions. The reviewAction
    // string ('approved' or 'rejected') determines the modal title, button color,
    // and the API action parameter.
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedClaim, setSelectedClaim] = useState(null);
    const [reviewAction, setReviewAction] = useState('');
    const [adminNotes, setAdminNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const adminApi = useAdminApi();

    // Fetches pending claims for the given page number.
    // Called on mount and again after each approve/reject action to refresh.
    const loadClaims = useCallback(
        async (page = 1) => {
            try {
                setLoading(true);
                setError('');

                const data = await adminApi.fetchPendingClaims({
                    page,
                    limit: 10,
                });

                setClaims(data.claims || []);
                setPagination(
                    data.pagination || { page: 1, pages: 1, total: 0 }
                );
            } catch (err) {
                setError('Failed to load claims');
                console.error(err);
            } finally {
                setLoading(false);
            }
        },
        [adminApi]
    );

    // Initial load on mount
    useEffect(() => {
        loadClaims(1);
    }, [loadClaims]);

    // Opens the shared review modal for either approve or reject.
    // Resets admin notes to empty so previous notes don't carry over.
    const openReviewModal = (claim, action) => {
        setSelectedClaim(claim);
        setReviewAction(action);
        setAdminNotes('');
        setShowReviewModal(true);
    };

    // Submits the approve/reject decision to the admin API.
    // The API call requires: orgId, claimId, action ('approved'/'rejected'),
    // and optional admin notes. On success, refreshes the claims list on the
    // current page so the resolved claim disappears from the queue.
    const handleReviewSubmit = async () => {
        if (!selectedClaim || !reviewAction) return;

        try {
            setSubmitting(true);
            await adminApi.reviewClaim(
                selectedClaim.organization._id,
                selectedClaim.claimId,
                reviewAction,
                adminNotes
            );

            setSuccess(
                `Claim ${reviewAction === 'approved' ? 'approved' : 'rejected'} successfully`
            );
            setShowReviewModal(false);
            // Refresh the current page to remove the processed claim
            loadClaims(pagination.page);

            // Auto-dismiss the success alert after 3 seconds
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to review claim');
        } finally {
            setSubmitting(false);
        }
    };

    // ---- Date Formatting Helpers ----

    // Formats a date string into a human-readable timestamp (e.g., "Jan 15, 2026, 02:30 PM")
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    // Produces a relative time string (e.g., "3d ago", "5h ago", "Just now")
    // for showing how long a claim has been pending at a glance
    const getTimeSince = (dateString) => {
        const now = new Date();
        const then = new Date(dateString);
        const diffMs = now - then;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) return `${diffDays}d ago`;
        if (diffHours > 0) return `${diffHours}h ago`;
        return 'Just now';
    };

    return (
        <>
            <Helmet>
                <title>Ownership Claims | Admin | RILE Connect</title>
            </Helmet>
            <Container className="py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 className="text-primary mb-0">Ownership Claims</h1>
                        <p className="text-muted mb-0">
                            Review and approve organization ownership requests
                        </p>
                    </div>
                    <Link to="/admin" className="btn btn-outline-secondary">
                        Back to Admin
                    </Link>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : /* Empty state: all claims have been reviewed */ claims.length ===
                  0 ? (
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center py-5">
                            <p className="text-muted mb-0">
                                No pending ownership claims
                            </p>
                        </Card.Body>
                    </Card>
                ) : (
                    <>
                        <Row>
                            {claims.map((claim) => (
                                <Col
                                    lg={6}
                                    key={claim.claimId}
                                    className="mb-4"
                                >
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="d-flex gap-3">
                                                    {claim.organization
                                                        .imageUrl ? (
                                                        <img
                                                            src={
                                                                claim
                                                                    .organization
                                                                    .imageUrl
                                                            }
                                                            alt={
                                                                claim
                                                                    .organization
                                                                    .name
                                                            }
                                                            className="admin-claim-thumbnail rounded"
                                                            onError={(e) => {
                                                                e.target.style.display =
                                                                    'none';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="admin-claim-placeholder rounded bg-light d-flex align-items-center justify-content-center">
                                                            <span className="text-muted">
                                                                {claim.organization.name.charAt(
                                                                    0
                                                                )}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <h5 className="mb-1">
                                                            {
                                                                claim
                                                                    .organization
                                                                    .name
                                                            }
                                                        </h5>
                                                        <Badge bg="primary">
                                                            {
                                                                claim
                                                                    .organization
                                                                    .primarySector
                                                            }
                                                        </Badge>
                                                    </div>
                                                </div>
                                                <small className="text-muted">
                                                    {getTimeSince(
                                                        claim.requestedAt
                                                    )}
                                                </small>
                                            </div>

                                            <div className="bg-light rounded p-3 mb-3">
                                                <div className="small">
                                                    <strong>Claimed by:</strong>{' '}
                                                    {claim.userName ||
                                                        claim.user}
                                                    {claim.userEmail && (
                                                        <span className="text-muted">
                                                            {' '}
                                                            ({claim.userEmail})
                                                        </span>
                                                    )}
                                                </div>
                                                {claim.roleAtOrg && (
                                                    <div className="small">
                                                        <strong>
                                                            Role at Org:
                                                        </strong>{' '}
                                                        {claim.roleAtOrg}
                                                    </div>
                                                )}
                                                {claim.additionalInfo && (
                                                    <div className="small mt-2">
                                                        <strong>
                                                            Additional Info:
                                                        </strong>
                                                        <br />
                                                        {claim.additionalInfo}
                                                    </div>
                                                )}
                                                <div className="small text-muted mt-2">
                                                    Requested:{' '}
                                                    {formatDate(
                                                        claim.requestedAt
                                                    )}
                                                </div>
                                            </div>

                                            {/* Verification method badge and action buttons.
                                                'email_domain' means the user's email domain
                                                matched the org's domain (higher confidence).
                                                'manual' means the admin needs to verify the
                                                claim based on the provided info alone. */}
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    {claim.verificationMethod ===
                                                    'email_domain' ? (
                                                        <Badge bg="success">
                                                            Email Domain Match
                                                        </Badge>
                                                    ) : (
                                                        <Badge bg="secondary">
                                                            Manual Verification
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="d-flex gap-2">
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() =>
                                                            openReviewModal(
                                                                claim,
                                                                'approved'
                                                            )
                                                        }
                                                    >
                                                        Approve
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() =>
                                                            openReviewModal(
                                                                claim,
                                                                'rejected'
                                                            )
                                                        }
                                                    >
                                                        Reject
                                                    </Button>
                                                    <Link
                                                        to={`/organizations/${claim.organization._id}`}
                                                        className="btn btn-outline-secondary btn-sm"
                                                        target="_blank"
                                                    >
                                                        View Org
                                                    </Link>
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="d-flex justify-content-center">
                                <Pagination>
                                    <Pagination.First
                                        onClick={() => loadClaims(1)}
                                        disabled={pagination.page === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() =>
                                            loadClaims(pagination.page - 1)
                                        }
                                        disabled={pagination.page === 1}
                                    />
                                    {[...Array(pagination.pages)].map(
                                        (_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={
                                                    i + 1 === pagination.page
                                                }
                                                onClick={() =>
                                                    loadClaims(i + 1)
                                                }
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        )
                                    )}
                                    <Pagination.Next
                                        onClick={() =>
                                            loadClaims(pagination.page + 1)
                                        }
                                        disabled={
                                            pagination.page === pagination.pages
                                        }
                                    />
                                    <Pagination.Last
                                        onClick={() =>
                                            loadClaims(pagination.pages)
                                        }
                                        disabled={
                                            pagination.page === pagination.pages
                                        }
                                    />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}

                {/* Review Modal: shared modal for both approve and reject actions.
                    Shows a summary of the claim, an info alert (approve only) explaining
                    the consequences, and an optional admin notes text area.
                    The action button color and label change based on reviewAction. */}
                <Modal
                    show={showReviewModal}
                    onHide={() => setShowReviewModal(false)}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {reviewAction === 'approved'
                                ? 'Approve Ownership Claim'
                                : 'Reject Ownership Claim'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedClaim && (
                            <>
                                <p>
                                    <strong>Organization:</strong>{' '}
                                    {selectedClaim.organization.name}
                                </p>
                                <p>
                                    <strong>Claimed by:</strong>{' '}
                                    {selectedClaim.userName ||
                                        selectedClaim.user}
                                    {selectedClaim.userEmail && (
                                        <span className="text-muted">
                                            {' '}
                                            ({selectedClaim.userEmail})
                                        </span>
                                    )}
                                </p>
                                {selectedClaim.roleAtOrg && (
                                    <p>
                                        <strong>Role:</strong>{' '}
                                        {selectedClaim.roleAtOrg}
                                    </p>
                                )}

                                {reviewAction === 'approved' && (
                                    <Alert variant="info">
                                        Approving this claim will make this user
                                        the owner of the organization. They will
                                        be able to edit the organization profile
                                        and manage members.
                                    </Alert>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        Admin Notes (optional)
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={adminNotes}
                                        onChange={(e) =>
                                            setAdminNotes(e.target.value)
                                        }
                                        placeholder={
                                            reviewAction === 'rejected'
                                                ? 'Reason for rejection...'
                                                : 'Any notes about this approval...'
                                        }
                                    />
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={() => setShowReviewModal(false)}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant={
                                reviewAction === 'approved'
                                    ? 'success'
                                    : 'danger'
                            }
                            onClick={handleReviewSubmit}
                            disabled={submitting}
                        >
                            {submitting ? (
                                <Spinner animation="border" size="sm" />
                            ) : reviewAction === 'approved' ? (
                                'Approve Claim'
                            ) : (
                                'Reject Claim'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </Container>
        </>
    );
}
